// src/components/AdminSettings/modals/ChoreModal.tsx
import React, { useState } from 'react';
import { IonItem, IonLabel, IonRange } from '@ionic/react';
import { Modal, ModalSection } from '../../ui/Modal';
import { TextInput } from '../../ui/TextInput';
import { FormSelect, type SelectOption } from '../../ui/FormSelect';
import { IconPickerModal, ClickableIcon, type IconValue } from '../../ui/IconPicker';
import { SCHEDULE_CATEGORY_OPTIONS } from '../constants';
import type { ChoreModalProps, ChoreFormData, ScheduleCategory } from '../types';

// Frequency options for the dropdown
type FrequencyValue = ChoreFormData['frequency'];

const FREQUENCY_OPTIONS: SelectOption<FrequencyValue>[] = [
  { id: 'daily', label: 'Daily', value: 'daily' },
  { id: 'weekly', label: 'Weekly', value: 'weekly' },
  { id: 'monthly', label: 'Monthly', value: 'monthly' },
  { id: 'as_needed', label: 'As Needed', value: 'as_needed' },
];

export const ChoreModal: React.FC<ChoreModalProps> = ({
  isOpen,
  onClose,
  editingChore,
  form,
  onFormChange,
  onSave,
  onDelete,
}) => {
  const [showIconPicker, setShowIconPicker] = useState(false);

  // Get the currently selected frequency option
  const selectedFrequency = FREQUENCY_OPTIONS.find(opt => opt.value === form.frequency) || null;

  // Parse the current icon value
  const currentIconValue: IconValue | undefined = form.icon
    ? { type: 'emoji', name: form.icon }
    : undefined;

  // Handle icon selection
  const handleIconSelect = (icon: IconValue) => {
    onFormChange({ ...form, icon: icon.name });
    setShowIconPicker(false);
  };

  // Toggle a category in the array
  const toggleCategory = (category: ScheduleCategory) => {
    const current = form.schedule_categories || [];
    const isSelected = current.includes(category);

    if (isSelected) {
      // Remove it (but ensure at least one remains)
      const newCategories = current.filter(c => c !== category);
      if (newCategories.length === 0) {
        // If removing last one, default to 'anytime'
        onFormChange({ ...form, schedule_categories: ['anytime'] });
      } else {
        onFormChange({ ...form, schedule_categories: newCategories });
      }
    } else {
      // Add it, but remove 'anytime' if adding a specific category
      let newCategories = [...current, category];
      if (category !== 'anytime' && newCategories.includes('anytime')) {
        newCategories = newCategories.filter(c => c !== 'anytime');
      }
      // If adding 'anytime', remove all others
      if (category === 'anytime') {
        newCategories = ['anytime'];
      }
      onFormChange({ ...form, schedule_categories: newCategories });
    }
  };

  return (
  <>
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      header={{ title: editingChore ? 'Edit Task' : 'Add Task' }}
      footer={{
        primaryAction: {
          label: editingChore ? 'Save' : 'Add Task',
          onClick: onSave,
          disabled: !form.name,
        },
        secondaryAction: { label: 'Cancel', onClick: onClose },
        ...(editingChore && {
          tertiaryAction: {
            label: 'Delete',
            onClick: onDelete,
            variant: 'danger' as const,
          },
        }),
      }}
    >
      <ModalSection spacing="normal">
        <TextInput
          label="Name"
          value={form.name}
          onChange={(v) => onFormChange({ ...form, name: v })}
          placeholder="e.g., Make Bed"
          variant="default"
        />
        <div style={{ marginTop: 16 }}>
          <label style={{
            display: 'block',
            fontFamily: 'var(--kff-data-font)',
            fontSize: 13,
            fontWeight: 500,
            color: 'var(--ion-color-medium)',
            marginBottom: 8,
          }}>
            Icon
          </label>
          <ClickableIcon
            icon={currentIconValue}
            fallback="📋"
            size="lg"
            onClick={() => setShowIconPicker(true)}
            showEditOverlay
          />
        </div>
        <div style={{ marginTop: 16 }}>
          <FormSelect<FrequencyValue>
            label="Frequency"
            value=""
            onChange={() => {}}
            options={FREQUENCY_OPTIONS}
            selectedOption={selectedFrequency}
            onSelect={(opt) => {
              onFormChange({ ...form, frequency: opt.value });
            }}
            placeholder="Select frequency..."
            variant="default"
            clearable={false}
            searchable={false}
          />
        </div>
        <div style={{ marginTop: 16 }}>
          <label style={{
            display: 'block',
            fontFamily: 'var(--kff-data-font)',
            fontSize: 13,
            fontWeight: 500,
            color: 'var(--ion-color-medium)',
            marginBottom: 8,
          }}>
            Schedule Categories
          </label>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
          }}>
            {SCHEDULE_CATEGORY_OPTIONS.map((opt) => {
              const isSelected = (form.schedule_categories || []).includes(opt.value);
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => toggleCategory(opt.value)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '6px 12px',
                    borderRadius: 16,
                    border: isSelected
                      ? '2px solid var(--ion-color-primary)'
                      : '2px solid var(--ion-color-step-200)',
                    backgroundColor: isSelected
                      ? 'var(--ion-color-primary-tint)'
                      : 'var(--ion-color-step-50)',
                    color: isSelected
                      ? 'var(--ion-color-primary-shade)'
                      : 'var(--ion-text-color)',
                    fontFamily: 'var(--kff-data-font)',
                    fontSize: 13,
                    fontWeight: isSelected ? 600 : 400,
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                >
                  {isSelected && (
                    <span style={{ fontSize: 12 }}>✓</span>
                  )}
                  {opt.label}
                </button>
              );
            })}
          </div>
          <p style={{
            fontSize: 11,
            color: 'var(--ion-color-medium)',
            marginTop: 6,
            marginBottom: 0,
          }}>
            Select multiple routines (e.g., "Brush Teeth" for both Morning & Bedtime)
          </p>
        </div>
        <IonItem style={{ marginTop: 16, '--background': 'transparent' }}>
          <IonLabel position="stacked">Points: {form.points_value}</IonLabel>
          <IonRange
            min={1}
            max={10}
            value={form.points_value}
            onIonChange={e => onFormChange({ ...form, points_value: e.detail.value as number })}
          />
        </IonItem>
      </ModalSection>
    </Modal>

    {/* Icon Picker Modal */}
    <IconPickerModal
      isOpen={showIconPicker}
      onClose={() => setShowIconPicker(false)}
      onSave={handleIconSelect}
      currentIcon={currentIconValue}
      title="Choose Task Icon"
      subtitle="Select an icon to represent this task"
      defaultTab="emoji"
    />
  </>
  );
};

export default ChoreModal;
